import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";
import { resolveApiUrl } from "./apiUrl";

type ApiFetchOptions = RequestInit & {
  skipAuth?: boolean;
  forceDirect?: boolean;
};

const API_GATEWAY_FALLBACK_TO_DIRECT =
  (process.env.REACT_APP_API_GATEWAY_FALLBACK_TO_DIRECT || "true").toLowerCase() === "true";

const GATEWAY_ROUTE_NOT_DEFINED = /current request is not defined by this api/i;

const getIdTokenSafe = async (forceRefresh = false) => {
  if (auth.currentUser) {
    try {
      return await auth.currentUser.getIdToken(forceRefresh);
    } catch (error) {
      console.error("Unable to read Firebase auth token:", error);
      return null;
    }
  }

  return new Promise<string | null>((resolve) => {
    let settled = false;
    let unsubscribe: (() => void) | null = null;

    const finish = (token: string | null) => {
      if (settled) return;
      settled = true;
      if (unsubscribe) unsubscribe();
      resolve(token);
    };

    const timeout = window.setTimeout(() => finish(null), 1200);

    unsubscribe = onAuthStateChanged(auth, async (user) => {
      window.clearTimeout(timeout);
      if (!user) {
        finish(null);
        return;
      }

      try {
        const token = await user.getIdToken(forceRefresh);
        finish(token);
      } catch (error) {
        console.error("Unable to read Firebase auth token:", error);
        finish(null);
      }
    });
  });
};

const shouldFallbackToDirect = async (response: Response) => {
  if (response.status !== 404) return false;

  try {
    const bodyText = await response.clone().text();
    return GATEWAY_ROUTE_NOT_DEFINED.test(bodyText);
  } catch {
    return false;
  }
};

export async function apiFetch(path: string, options: ApiFetchOptions = {}) {
  const { skipAuth = false, forceDirect = false, headers: incomingHeaders, ...rest } = options;

  const buildHeaders = async (forceRefresh: boolean) => {
    const headers = new Headers(incomingHeaders || {});
    if (!skipAuth) {
      const token = await getIdTokenSafe(forceRefresh);
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
    }
    return headers;
  };

  const endpoint = resolveApiUrl(path, { forceDirect });

  const doFetch = async (headers: Headers) => {
    const requestInit: RequestInit = { ...rest, headers };
    try {
      const response = await fetch(endpoint.url, requestInit);
      if (!endpoint.useGateway || !API_GATEWAY_FALLBACK_TO_DIRECT) {
        return response;
      }
      if (await shouldFallbackToDirect(response)) {
        const directEndpoint = resolveApiUrl(path, { forceDirect: true });
        return fetch(directEndpoint.url, requestInit);
      }
      return response;
    } catch (error) {
      if (endpoint.useGateway && API_GATEWAY_FALLBACK_TO_DIRECT && error instanceof TypeError) {
        const directEndpoint = resolveApiUrl(path, { forceDirect: true });
        return fetch(directEndpoint.url, requestInit);
      }
      throw error;
    }
  };

  const headers = await buildHeaders(false);
  const response = await doFetch(headers);

  // On 401, force-refresh the token once and retry — catches stale cached tokens.
  if (response.status === 401 && !skipAuth) {
    const refreshedHeaders = await buildHeaders(true);
    return doFetch(refreshedHeaders);
  }

  return response;
}
